/**
 * src/db/migrations.ts — sequential, single-step schema migrations (§9.2).
 *
 * Each user's data lives only in their browser, so migrations run client-side, lazily,
 * and must never lose data. An ordered registry of pure `migrate[N→N+1]` functions
 * upgrades a record from its stored `schemaVersion` to CURRENT, after which it is
 * validated against the current schema. Records that fail are quarantined (never
 * deleted); records saved by a newer app version load read-only (passthrough).
 */
import { CURRENT_SCHEMA_VERSION, scenarioSchema } from '@/schema';
import type { Scenario } from '@/types';

/** A single-step migration from version N to N+1. Pure; operates on raw JSON. */
type Migration = (raw: Record<string, unknown>) => Record<string, unknown>;

/** Registry keyed by source version: `MIGRATIONS[n]` upgrades a v`n` record to v`n+1`. */
export const MIGRATIONS: Record<number, Migration> = {
  // 1 → 2: introduce collateral buckets and the liquidation policy (additive).
  1: (raw) => {
    const inputs = (raw.inputs ?? {}) as Record<string, unknown>;
    return {
      ...raw,
      schemaVersion: 2,
      inputs: {
        ...inputs,
        useBuckets: inputs.useBuckets ?? false,
        collateralBuckets: inputs.collateralBuckets ?? [],
        liquidationPolicy: inputs.liquidationPolicy ?? 'schwab_liquidity',
      },
    };
  },
  // 2 → 3: introduce the provider abstraction; existing scenarios are Schwab PAL.
  2: (raw) => {
    const inputs = (raw.inputs ?? {}) as Record<string, unknown>;
    return {
      ...raw,
      schemaVersion: 3,
      inputs: { ...inputs, providerId: inputs.providerId ?? 'schwab_pal' },
    };
  },
  // 3 → 4: retire the provider registry (§5). The spread is now entered directly rather
  // than derived from a bundled tier table, so `providerId` and the IAP-discount input
  // `qualifyingAssets` are dropped and the two fields a provider used to supply are
  // seeded from the schema defaults. A v3 scenario's APR may therefore shift; the
  // reconstructed spread is a starting point the user edits to their quoted rate.
  3: (raw) => {
    const { providerId: _providerId, qualifyingAssets: _qualifyingAssets, ...inputs } = (raw.inputs ??
      {}) as Record<string, unknown>;
    return {
      ...raw,
      schemaVersion: 4,
      inputs: {
        ...inputs,
        spreadPct: inputs.spreadPct ?? 2.9,
        // Both shipped providers set a $100,000 minimum loan value of collateral.
        minimumLoanValue: inputs.minimumLoanValue ?? 100_000,
      },
    };
  },
  // 4 → 5: the rate path stopped being SOFR-specific. SOFR only PRE-FILLS the benchmark
  // (§7.1) — a Prime-based or house-rate line is modeled by editing it — so the persisted
  // field names were renamed to match. Pure key renames, no value changes: a v4 scenario
  // projects identically after this step.
  //
  // The OLD key names are written as string literals on purpose. A migration describes a
  // shape that no longer exists anywhere in the code, so it must not move when identifiers
  // are renamed — a mechanical rename sweep already "fixed" these once and silently broke
  // the step, which is exactly the failure this pattern prevents.
  4: (raw) => {
    const inputs = (raw.inputs ?? {}) as Record<string, unknown>;
    const old = { ...((inputs.ratePath ?? {}) as Record<string, unknown>) };

    /** Take the new key if the record already has it, else the legacy one; drop the legacy. */
    const carry = (nextKey: string, legacyKey: string): unknown => {
      const value = old[nextKey] ?? old[legacyKey];
      delete old[legacyKey];
      return value;
    };

    const baseRatePct = carry('baseRatePct', 'baseSofr') ?? 0;
    const floorPct = carry('floorPct', 'sofrFloorPct');
    const capPct = carry('capPct', 'sofrCapPct');
    const legacyAnchors = old.anchors;
    delete old.anchors;

    const ratePath: Record<string, unknown> = { ...old, baseRatePct };
    // Only set the optional bounds when the record actually carried them: writing an
    // explicit null would turn an unbounded path into one floored at 0, silently changing
    // the projection.
    if (floorPct !== undefined) ratePath.floorPct = floorPct;
    if (capPct !== undefined) ratePath.capPct = capPct;
    if (Array.isArray(legacyAnchors)) {
      ratePath.anchors = legacyAnchors.map((a) => {
        const anchor = { ...((a ?? {}) as Record<string, unknown>) };
        const baseRate = anchor.baseRate ?? anchor['sofr'] ?? 0;
        delete anchor['sofr'];
        return { ...anchor, baseRate };
      });
    }
    return { ...raw, schemaVersion: 5, inputs: { ...inputs, ratePath } };
  },
};

export type MigrateResult =
  | { status: 'ok'; scenario: Scenario; upgraded: boolean }
  | { status: 'future'; scenario: Scenario }
  | { status: 'quarantine'; raw: unknown; error: string };

/** The stored schema version of a raw record (defaults to 1 for legacy records). */
function versionOf(raw: unknown): number {
  if (raw && typeof raw === 'object' && 'schemaVersion' in raw) {
    const v = (raw as { schemaVersion: unknown }).schemaVersion;
    if (typeof v === 'number' && Number.isFinite(v)) return v;
  }
  return 1;
}

/**
 * Migrate a raw record up to the current schema, then validate (§9.2).
 * - Newer-than-current records load read-only via passthrough (no crash, no downgrade).
 * - Anything that still fails validation is quarantined, never dropped.
 */
export function migrateRecord(raw: unknown): MigrateResult {
  const fromVersion = versionOf(raw);

  if (fromVersion > CURRENT_SCHEMA_VERSION) {
    const parsed = scenarioSchema.passthrough().safeParse(raw);
    if (parsed.success) return { status: 'future', scenario: parsed.data as Scenario };
    return { status: 'quarantine', raw, error: 'Newer record failed passthrough validation.' };
  }

  try {
    let current = (raw ?? {}) as Record<string, unknown>;
    for (let v = fromVersion; v < CURRENT_SCHEMA_VERSION; v++) {
      const step = MIGRATIONS[v];
      if (!step) {
        return { status: 'quarantine', raw, error: `No migration registered for version ${v}.` };
      }
      current = step(current);
    }
    const parsed = scenarioSchema.safeParse(current);
    if (parsed.success) {
      return { status: 'ok', scenario: parsed.data, upgraded: fromVersion < CURRENT_SCHEMA_VERSION };
    }
    return { status: 'quarantine', raw, error: parsed.error.issues.map((i) => i.message).join('; ') };
  } catch (err) {
    return { status: 'quarantine', raw, error: err instanceof Error ? err.message : 'migration threw' };
  }
}
